import { Flex } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslate } from "@refinedev/core";
import useNotification from "antd/es/notification/useNotification";

import { BodyView, ITEM_TYPES, TemplateType } from "../../types";
import { TransitionPreview } from "./Preview/TransitionPreview";
import {
  DEFAULT_COLOR,
  MAX_INTRODUCTION_DURATION_IN_FRAMES,
  MAX_CLOSING_DURATION_IN_FRAMES,
  VIDEO_FPS,
  SCENE_SCROLL_MARGIN_TOP,
  TEMPLATE_OUTLINE_WIDTH,
} from "../../constants";
import { TemplateCustomTexts } from "./Preview/components/TemplateCustomTexts";
import { TemplateImages } from "./Preview/components/TemplateImages";
import { TemplateVideos } from "./Preview/components/TemplateVideos";
import { BackgroundPreviewDynamic } from "./Preview/components/BackgroundPreview";
import { ColoredBackground } from "./Preview/components/ColoredBackground";
import {
  useTemplateEditorStore,
  useTemplateEditorStoreApi,
} from "../../store/useTemplateEditorStore";
import { useTemplateData } from "../../hooks/useTemplateData";
import { useTemplatesStatusStore } from "../../store/useTemplatesStatusStore";
import { TemplatePlayer } from "./TemplatePlayer";
import { TemplateShapes } from "./Preview/components/TemplateShapes";
import { MoveableRegistryProvider } from "./Preview/moveableRegistry";
import { MoveableLayer } from "./Preview/components/MoveableLayer";
import { ChapterPreview } from "./Preview/ChapterPreview";
import { ItemControlsPopover } from "./Preview/components/ItemControlsPopover";
import { StyledPreview, StyledWrapper } from "./SlideEditableTemplate";
import { StoryboardSection } from "pages/media/types/";

type EditableTemplateProps = {
  chapterScript?: string;
  previewWidth: number;
  previewHeight: number;
  updateStoryboardSection?: (
    sectionId: string,
    updates: Partial<StoryboardSection>
  ) => void;
};

export const EditableTemplate = ({
  chapterScript = "",
  previewWidth,
  previewHeight,
  updateStoryboardSection,
}: EditableTemplateProps) => {
  const {
    mediaGenerationSettingsData: { mediaAspectRatio },
    storyboard,
  } = useTemplateData();

  const {
    currentChapter,
    templateState,
    updateTemplateState,
    setIntro,
    setClosing,
    setTransition,
  } = useTemplateEditorStore((store) => ({
    currentChapter: store.currentChapter,
    templateState: store.templateState,
    updateTemplateState: store.updateTemplateState,
    setIntro: store.setIntro,
    setClosing: store.setClosing,
    setTransition: store.setTransition,
  }));

  const {
    images,
    videos,
    customTexts,
    shapes,
    background,
    transition,
    currentItem,
  } = templateState;

  const { type: view } = currentChapter;

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const templateRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const isInitialScrollRunRef = useRef(true);
  const templateEditorStoreApi = useTemplateEditorStoreApi();

  /**
   * Scrolls the template into view on aspect-ratio changes, and on the initial
   * mount only when the selection came from a thumbnail click. Scene clicks
   * and scroll-stop activations leave the user's scroll position alone.
   */
  useEffect(() => {
    if (isInitialScrollRunRef.current) {
      isInitialScrollRunRef.current = false;
      const source = templateEditorStoreApi.getState().selectionSource;
      if (source === "scene") return;
    }
    const templateElement = templateRef.current;
    if (templateElement) {
      templateElement.style.setProperty(
        "scroll-margin-block-start",
        `${SCENE_SCROLL_MARGIN_TOP}px`
      );
      // Use double rAF to ensure layout has settled before scrolling
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          templateElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      });
    }
  }, [mediaAspectRatio, templateEditorStoreApi]);

  const t = useTranslate();
  const [api, contextHolder] = useNotification();

  const video = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const handlePlayPause = async () => {
    if (isPlaying) {
      video.current?.pause();
      setIsPlaying(false);
    } else if (video.current) {
      video.current.volume = 0;
      if (currentTime < video.current.duration) await video.current.play();
      setIsPlaying(true);
    }
  };

  const handleRefTimeUpdate = (
    current?: HTMLAudioElement | HTMLVideoElement | null
  ) => {
    if (current) {
      if (Math.abs(current.currentTime - duration) < 0.1) {
        setCurrentTime(0);
        setIsPlaying(false);
      } else {
        setCurrentTime(current.currentTime);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (video.current) {
      const videoDuration = video.current.duration;
      setDuration(videoDuration);

      const assetDurationInFrames = {
        assetDuration: Math.round(videoDuration * VIDEO_FPS),
      };

      if (view === TemplateType.INTRODUCTION) {
        setIntro(assetDurationInFrames);
      } else if (view === TemplateType.CLOSING) {
        setClosing(assetDurationInFrames);
      } else if (view === TemplateType.TRANSITION) {
        setTransition(assetDurationInFrames);
      }
    }
  };

  const removeBackgroundLabel: BodyView = useMemo(() => {
    if (view === TemplateType.CHAPTER) {
      return background.bodyView;
    } else if (view === TemplateType.TRANSITION) {
      return transition.view;
    }
    return "Color";
  }, [view, background, transition]);

  const showDeleteButton = useMemo(() => {
    if (currentItem?.type !== ITEM_TYPES.Background) return false;
    if (view === TemplateType.CHAPTER) {
      return background.bodyView !== "Color";
    } else if (view === TemplateType.TRANSITION) {
      return transition.view !== "Color";
    }
    return false;
  }, [currentItem, view, background, transition]);

  const handleBackgroundRemove = () => {
    const updatedState = {
      background: {
        bodyView: "Color" as const,
        bodyImage: undefined,
        bodyImageAsset: undefined,
        bodyVideo: undefined,
        bodyVideoAsset: undefined,
      },
      transition:
        view === TemplateType.TRANSITION
          ? {
              ...templateState.transition,
              view: "Color" as const,
              assetId: undefined,
              assetPath: undefined,
            }
          : templateState.transition,
    };
    updateTemplateState(updatedState);
    addTemplateUpdate();
  };

  const showVideoControls = background.bodyView === "Video";

  const validateSectionUpdate = (
    sectionUpdates: Partial<StoryboardSection>
  ) => {
    const { topic } = sectionUpdates;
    if (!topic) return false;
    const topicExists = storyboard?.sections.some(
      (s) => s.topic === topic && s.id !== currentChapter.sectionId
    );
    if (topicExists) {
      api.warning({
        message: t("storyboard.editor.Section.chapterExists"),
      });
      return false;
    }
    return true;
  };

  const maxDuration =
    view === TemplateType.INTRODUCTION
      ? MAX_INTRODUCTION_DURATION_IN_FRAMES / VIDEO_FPS
      : MAX_CLOSING_DURATION_IN_FRAMES / VIDEO_FPS;

  return (
    <Flex
      ref={templateRef}
      vertical
      justify="center"
      align="center"
      gap={8}
      style={{
        height: "100%",
        width: "100%",
        overflow: "hidden",
        padding: TEMPLATE_OUTLINE_WIDTH,
        marginBlockEnd: showVideoControls ? 8 : 18,
      }}
    >
      {contextHolder}
      <StyledWrapper
        className="template-preview"
        data-testid="template-preview"
      >
        <ItemControlsPopover
          open={showDeleteButton}
          placement="rightTop"
          arrow={false}
          onDelete={handleBackgroundRemove}
          getPopupContainer={(triggerNode) => {
            const container =
              document.getElementsByClassName("template-preview")[0]
                ?.parentElement;
            if (container) {
              return container;
            }
            return triggerNode;
          }}
          deleteButtonTooltip={t(
            "components.templates.templatePreview.removeBackground",
            {
              bodyView: t(
                `components.templates.settings.transitionView.${removeBackgroundLabel.toLowerCase()}`
              ).toLowerCase(),
            }
          )}
          canRotate={false}
          canDuplicate={false}
        >
          <StyledPreview
            ref={previewRef}
            data-interactive
            width={previewWidth}
            height={previewHeight}
            $backgroundColor={DEFAULT_COLOR}
          >
            <MoveableRegistryProvider>
              <BackgroundPreviewDynamic
                ref={video}
                handleTimeUpdate={() => handleRefTimeUpdate(video.current)}
                handleLoadedMetadata={handleLoadedMetadata}
              />
              <ColoredBackground
                width={previewWidth}
                height={previewHeight}
                backgroundColor={background.bodyColor}
              />
              <TemplateImages
                templateImages={images}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
                editable
              />
              <TemplateVideos
                templateVideos={videos}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
                editable
              />
              <TemplateCustomTexts
                customTexts={customTexts}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
                editable
              />
              <TemplateShapes
                shapes={shapes}
                previewWidth={previewWidth}
                previewHeight={previewHeight}
                editable
              />
              {view === TemplateType.TRANSITION && (
                <TransitionPreview
                  previewWidth={previewWidth}
                  previewHeight={previewHeight}
                  onSectionUpdate={updateStoryboardSection}
                  validateSectionUpdate={validateSectionUpdate}
                />
              )}
              {view === TemplateType.CHAPTER && (
                <ChapterPreview
                  text={chapterScript}
                  previewWidth={previewWidth}
                  previewHeight={previewHeight}
                />
              )}
              <MoveableLayer
                previewWidth={previewWidth}
                previewHeight={previewHeight}
                rootRef={previewRef}
              />
            </MoveableRegistryProvider>
          </StyledPreview>
        </ItemControlsPopover>
      </StyledWrapper>
      {showVideoControls && (
        <TemplatePlayer
          data-interactive
          previewWidth={previewWidth}
          currentTime={currentTime}
          duration={duration}
          isPlaying={isPlaying}
          onSliderChange={(value: number) => {
            if (video.current) {
              setCurrentTime(value);
              video.current.currentTime = value;
            }
          }}
          onPlayPauseClick={handlePlayPause}
          warningMessage={
            (view === TemplateType.INTRODUCTION ||
              view === TemplateType.CLOSING) &&
            duration > maxDuration
              ? t("components.templates.player.durationExceedsMaxWarning", {
                  maxDuration,
                })
              : undefined
          }
        />
      )}
    </Flex>
  );
};
